import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Grid, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

export interface ProjectDataItem {
  image: string;
  projectName: string;
  description: string;
  technologies?: string[];
  icon?: string;
  industry?: string;
  client?: string;
  role?: string;
  location?: string;
  quote?: string;
}

export interface EnhancedProjectsSectionProps {
  projectData: ProjectDataItem[];
}

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2),
  maxWidth: 760,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.65)",
  maxWidth: 640,
});

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: theme.spacing(3),
  flexWrap: "wrap",
  marginBottom: theme.spacing(5),
  [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(4) },
}));

const Rating = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 14px",
  borderRadius: 999,
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  border: "1px solid rgba(47, 129, 173, 0.18)",
});

const RatingScore = styled("span")({
  fontSize: "0.92rem",
  fontWeight: 700,
  color: "#1d648c",
});

const RatingLabel = styled("span")({
  fontSize: "0.82rem",
  color: "rgba(15, 23, 42, 0.65)",
});

const Card = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  backgroundColor: "#fff",
  borderRadius: 10,
  overflow: "hidden",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  cursor: "pointer",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 12px 28px -16px rgba(15, 23, 42, 0.2)",
    "& .case-image": { transform: "scale(1.04)" },
  },
  "&:focus-visible": {
    outline: "2px solid #2f81ad",
    outlineOffset: 2,
  },
});

const Thumb = styled("div")({
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 10",
  overflow: "hidden",
  backgroundColor: "#f1f5f9",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.4s ease",
  },
});

const IndustryChip = styled("span")({
  position: "absolute",
  top: 14,
  left: 14,
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#fff",
  padding: "4px 10px",
  borderRadius: 999,
  backgroundColor: "rgba(13, 22, 32, 0.85)",
  backdropFilter: "blur(4px)",
});

const Body = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  padding: theme.spacing(3, 3),
}));

const ProjectName = styled(Typography)({
  fontSize: "1.18rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
  letterSpacing: "-0.01em",
  lineHeight: 1.3,
});

const ProjectCopy = styled(Typography)({
  fontSize: "0.92rem",
  lineHeight: 1.6,
  color: "rgba(15, 23, 42, 0.65)",
  marginBottom: 18,
});

const QuoteBlock = styled(Box)({
  position: "relative",
  padding: "16px 16px 16px 20px",
  marginTop: "auto",
  marginBottom: 18,
  borderLeft: "2px solid #2f81ad",
  backgroundColor: "rgba(47, 129, 173, 0.04)",
  borderRadius: "0 6px 6px 0",
});

const QuoteText = styled(Typography)({
  fontSize: "0.88rem",
  fontStyle: "italic",
  lineHeight: 1.55,
  color: "rgba(15, 23, 42, 0.78)",
});

const Meta = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "0.78rem",
  color: "rgba(15, 23, 42, 0.55)",
});

const MetaRole = styled("span")({
  fontWeight: 600,
  color: "rgba(15, 23, 42, 0.75)",
});

const OutlineButton = styled(Button)({
  padding: "11px 24px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  color: "#0f172a",
  border: "1px solid rgba(15, 23, 42, 0.18)",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: "#0f172a",
    color: "#fff",
    borderColor: "#0f172a",
  },
  transition: "all 0.2s ease",
});

const EnhancedProjectsSection: React.FC<EnhancedProjectsSectionProps> = ({
  projectData,
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const goToPortfolio = () => navigate("/portfolio");

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  return (
    <Box
      ref={ref}
      component="section"
      aria-labelledby="projects-heading"
      sx={{
        py: { xs: 8, md: 11 },
        backgroundColor: "#f8fafc",
      }}
    >
      <Container maxWidth="lg">
        <HeaderRow
          sx={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
          }}
        >
          <Box>
            <Label>Case studies</Label>
            <SectionTitle id="projects-heading" variant="h2">
              Software we've shipped for product teams
            </SectionTitle>
            <Lead>
              A selection of products our senior engineers have designed, built,
              and delivered with clients across North America.
            </Lead>
          </Box>
          <Rating>
            <RatingScore>4.8/5</RatingScore>
            <RatingLabel>Average client rating</RatingLabel>
          </Rating>
        </HeaderRow>

        <Grid container spacing={2.5}>
          {projectData.map((project, i) => (
            <Grid item xs={12} sm={6} md={4} key={project.projectName}>
              <Box
                sx={{
                  height: "100%",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(12px)",
                  transition: `opacity 0.45s ease ${
                    0.05 + i * 0.06
                  }s, transform 0.45s ease ${0.05 + i * 0.06}s`,
                }}
              >
                <Card
                  role="link"
                  tabIndex={0}
                  onClick={goToPortfolio}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      goToPortfolio();
                    }
                  }}
                  aria-label={`View ${project.projectName} case study`}
                >
                  <Thumb>
                    <img
                      className="case-image"
                      src={project.image}
                      alt={project.projectName}
                      loading="lazy"
                    />
                    {project.industry && (
                      <IndustryChip>{project.industry}</IndustryChip>
                    )}
                  </Thumb>
                  <Body>
                    <ProjectName variant="h3">{project.projectName}</ProjectName>
                    <ProjectCopy>{project.description}</ProjectCopy>
                    {project.quote && (
                      <QuoteBlock>
                        <FormatQuoteIcon
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 10,
                            color: "rgba(47, 129, 173, 0.25)",
                            fontSize: 22,
                          }}
                        />
                        <QuoteText>"{project.quote}"</QuoteText>
                      </QuoteBlock>
                    )}
                    <Meta>
                      <MetaRole>
                        {project.role || project.client || "—"}
                      </MetaRole>
                      <span>{project.location || ""}</span>
                    </Meta>
                  </Body>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: { xs: 5, md: 7 },
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton onClick={goToPortfolio} endIcon={<ArrowForwardIcon />}>
            All case studies
          </OutlineButton>
        </Box>
      </Container>
    </Box>
  );
};

export default EnhancedProjectsSection;
